/**
 * Story-level transcript editing lock (2026-09-24, Toro Japanese-captions
 * incident), gated by the LaunchDarkly flag `enableStoryTranscriptEditing`.
 *
 * A word-count change on the STORY transcript — "Replace Term" with fewer or
 * more words, a punctuation insert — renumbers every later element's
 * `initial_index`. The caption producer and the caption reconcile join
 * translated phrases to words by that position, and a clip keeps a frozen copy
 * of the transcript from the moment it was cut, so an existing translation or
 * clip drifts: translated rows land on the wrong words, phrases vanish, and
 * source-language rows leak into a translated track. Only the Transcript &
 * Speakers tab renumbers before Generate; Creator Studio and the clip popup do
 * not, so "replace first, then translate from Creator Studio" breaks captions on
 * the very first produce.
 *
 * Until captions no longer depend on positions (PLAT-10786), no story-level
 * surface may change the story transcript's element count. Word fixes go
 * through the clip editor (a clip-local copy that never renumbers the story);
 * translation fixes stay in the Translation Review workspace.
 *
 * The flag is the CAPABILITY, not the lock: `enableStoryTranscriptEditing`
 * absent, unreachable or false ⇒ locked, which is the safe direction. Turn it
 * on per target in LaunchDarkly to re-enable story-level editing.
 *
 * Covered: "Replace Term" on the Transcript & Speakers tab, "Replace Term" on the
 * Review workspace's original pane, and punctuation by key press on the story
 * tab (refused at the command level). Deliberately NOT covered as rare: paste
 * before/after, re-transcription, XML import.
 */

/** The flag slice the lock reads; `useFlagsCombination` supplies it. */
export interface StoryTranscriptEditingFlags {
  enableStoryTranscriptEditing?: boolean;
}

/** True unless the flag explicitly enables story-level transcript editing. */
export function isStoryTranscriptEditingLocked(
  flags: StoryTranscriptEditingFlags | undefined,
): boolean {
  return !(flags?.enableStoryTranscriptEditing ?? false);
}

/**
 * Editor commands that change the story transcript's ELEMENT COUNT. While the
 * lock is on, the story-level adapter refuses these at `executeCommand`,
 * whatever surface issued them — the hidden "Replace Term" rows are the visible
 * half of the lock, this is the enforced half. It also covers punctuation by
 * key press (Comma / Period / Enter → `TranscriptAddPunctuationCommand`), which
 * has no menu row to hide.
 *
 * Deliberately absent: `TranscriptRemoveCommand` (marks words removed, never
 * splices, and is unreachable on the story tab anyway) and
 * `TranscriptPasteCommand` (rare on the story tab; add it here if that changes).
 */
export const STORY_ELEMENT_COUNT_CHANGING_COMMANDS: ReadonlySet<string> =
  new Set([
    'TranscriptTextReplaceCommand',
    'TranscriptMultiSegmentTextReplaceCommand',
    'TranscriptMultiRangeTextReplaceCommand',
    'TranscriptAddPunctuationCommand',
  ]);

/** True when the story-level editor must refuse this command under the lock. */
export function isRefusedStoryTranscriptCommand(
  commandName: string,
  locked: boolean,
): boolean {
  return locked && STORY_ELEMENT_COUNT_CHANGING_COMMANDS.has(commandName);
}
